'use client';

import type { ConsentAgreementItemInput } from '__generated__/graphql';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { languageCode } from 'utils/locale';
import type { CmsProduct } from '../cmsService/types';
import {
  type ApprovalOddsConsentResult,
  type ApprovalOddsEntry,
  ensureFetch,
  getEntry,
  invalidate,
  sendConsent as sendConsentStore,
  subscribe,
} from './approvalOddsStore';
import type {
  ApprovalDegree,
  ApprovalOddsResult,
  ApprovalUserState,
} from './types';
import { useIdentity } from '$/modules/auth/hooks';

export type UseApprovalOddsReturn = {
  data: {
    // Keyed by product title (the join key between CmsProduct and ApprovalOdd.productId)
    odds: Record<string, ApprovalDegree>;
    lastUpdated: string | null;
    userState: ApprovalUserState;
    agreements: ApprovalOddsResult['chcConsentAgreements']['agreements'];
    consents: ApprovalOddsResult['chcUserConsents']['consents'];
  } | null;
  loading: boolean;
  error: unknown;
  refresh: () => void;
  sendConsent: (
    consentAgreements: ReadonlyArray<ConsentAgreementItemInput>,
  ) => Promise<ApprovalOddsConsentResult>;
};

const GUEST_DATA: UseApprovalOddsReturn['data'] = {
  odds: {},
  lastUpdated: null,
  userState: 'guest',
  agreements: [],
  consents: [],
};

export function useApprovalOdds(
  products: CmsProduct[],
  shouldFetch: boolean,
  langPrefix: string,
): UseApprovalOddsReturn {
  const identity = useIdentity();
  const sub = identity?.sub;
  const langCode = useMemo(() => languageCode(langPrefix), [langPrefix]);

  const [entry, setEntry] = useState<ApprovalOddsEntry | undefined>(() =>
    sub && shouldFetch ? getEntry(sub, langCode) : undefined,
  );

  useEffect(() => {
    if (!sub || !shouldFetch) {
      // React bails out on identical state, so setting undefined is safe.
      setEntry(undefined);
      return;
    }
    const onChange = () => setEntry(getEntry(sub, langCode));
    const unsubscribe = subscribe(sub, langCode, onChange);
    ensureFetch(sub, langCode);
    // Sync once after subscribing so we pick up the entry that
    // `ensureFetch` may have just installed (loading) or that already
    // existed (ready/error).
    onChange();
    return unsubscribe;
  }, [sub, shouldFetch, langCode]);

  const data = useMemo<UseApprovalOddsReturn['data']>(() => {
    if (!sub) return GUEST_DATA;
    if (!entry) return null;
    if (entry.status === 'ready') {
      return {
        odds: mapToData(entry.result, products),
        lastUpdated: entry.result.lastUpdated,
        userState: entry.result.userState,
        agreements: entry.result.chcConsentAgreements.agreements,
        consents: entry.result.chcUserConsents.consents,
      };
    }
    if (entry.status === 'error') {
      return GUEST_DATA;
    }
    return null;
  }, [entry, products, sub]);

  const refresh = useCallback(() => {
    if (sub) invalidate(sub, langCode);
  }, [sub, langCode]);

  const sendConsent = useCallback(
    async (
      consentAgreements: ReadonlyArray<ConsentAgreementItemInput>,
    ): Promise<ApprovalOddsConsentResult> => {
      if (!sub) {
        throw new Error('No authenticated user');
      }
      return sendConsentStore(sub, langCode, consentAgreements);
    },
    [sub, langCode],
  );

  return {
    data,
    loading: !!sub && entry?.status === 'loading',
    error: entry?.status === 'error' ? entry.error : null,
    refresh,
    sendConsent,
  };
}

const mapToData = (
  result: ApprovalOddsResult,
  products: CmsProduct[],
): Record<string, ApprovalDegree> => {
  // First index wire odds into exact matches and issuer-based fallback.
  // Only one rating per issuer, so store the first (and only) match.
  const byProductId = new Map<string, ApprovalDegree>();
  const byIssuerName = new Map<string, ApprovalDegree>();
  for (const item of result.odds) {
    if (item.productId) {
      byProductId.set(item.productId, item.approvalDegree);
      continue;
    }
    if (!item.issuerName) continue;
    const issuer = item.issuerName.toLowerCase();
    // Only record the first occurrence (there should only be one per issuer).
    if (!byIssuerName.has(issuer)) {
      byIssuerName.set(issuer, item.approvalDegree);
    }
  }

  // Then walk products once and assign by productId first, issuer fallback second.
  const odds: Record<string, ApprovalDegree> = {};
  for (const product of products) {
    if (!product.title) continue;

    const exactMatch = byProductId.get(product.title);
    if (exactMatch !== undefined) {
      odds[product.title] = exactMatch;
      continue;
    }

    const uid = product.provider?.provider_uid?.toLowerCase();
    if (!uid) continue;
    const fallbackMatch = byIssuerName.get(uid);
    if (fallbackMatch !== undefined) {
      odds[product.title] = fallbackMatch;
    }
  }

  return odds;
};
